import React, { useEffect, useState } from "react";
import { Button, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { maxBounds } from "../../constants/contants";
import { ModalCloseIcon } from "../../constants/svg";
import {
  CreateCategory,
  UpdateCategory,
} from "../../services/categories/categories";
import { setReloadCategories } from "../../store/slices/globalSlice";
import { all_category_type } from "../../utils/constants";
import { toastMessage } from "../../utils/helper";

const AddCategoryModal = (props) => {
  const dispatch = useDispatch();
  const { currentSalonId, reloadCategories } = useSelector(
    (state) => state.global,
  );
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: { name: "" },
  });

  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [disable, setDisable] = useState(true);
  const watchingName = watch("name");

  useEffect(() => {
    if (props?.isUpdateCategory && props?.selectedData) {
      setValue("name", props?.selectedData?.name || "");
      const existingTags = (
        props?.selectedData?.categoryTags ||
        props?.selectedData?.category_tags ||
        []
      )
        .map((tag) => tag.name)
        .filter(Boolean);
      setTags(existingTags);
    } else {
      setValue("name", "");
      setTags([]);
      setTagInput("");
    }
  }, [props?.selectedData, props?.isUpdateCategory, setValue]);

  useEffect(() => {
    if (watchingName?.trim() && !loading) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [watchingName, loading]);

  const addTagFromInput = () => {
    const value = tagInput.trim();
    if (!value) return;
    const exists = tags.some(
      (tag) => tag.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) {
      setTags((prev) => [...prev, value]);
    }
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTagFromInput();
    }
  };

  const removeTag = (index) => {
    setTags((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setDisable(true);

      const pendingTag = tagInput.trim();
      const finalTags = [...tags];
      if (
        pendingTag &&
        !finalTags.some((tag) => tag.toLowerCase() === pendingTag.toLowerCase())
      ) {
        finalTags.push(pendingTag);
      }

      const payload = {
        name: data?.name?.trim(),
        type: all_category_type.RFQ,
        salon_id: currentSalonId,
        tags: finalTags,
      };

      let res;
      if (props?.isUpdateCategory) {
        res = await UpdateCategory(props?.selectedData?.id, payload);
      } else {
        res = await CreateCategory(payload);
      }

      if (res?.data?.status === false) {
        toastMessage("warning", res?.data?.message || "Something went wrong");
        setDisable(false);
        setLoading(false);
        return;
      }

      toastMessage("success", res?.data?.message);
      dispatch(setReloadCategories(reloadCategories + 1));
      props?.onSaved?.();
      props.setModalShow?.(false);
      setTags([]);
      setTagInput("");
      setLoading(false);
      setDisable(false);
    } catch (e) {
      setDisable(false);
      setLoading(false);
      toastMessage(
        "warning",
        e?.response?.data?.message || e.message || "Something went wrong",
      );
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="create-category-form">
      <div className={`modal-body ${props.customModalBody || ""}`}>
        <Row>
          <Col lg={12} className="mb-3">
            <FloatingLabel
              controlId="categoryName"
              label="Category Name"
              className="custom-textarea"
            >
              <Form.Control
                type="text"
                placeholder="Category Name"
                autoComplete="off"
                role="presentation"
                className={errors.name ? "hasError" : ""}
                maxLength={maxBounds.NAME}
                {...register("name", {
                  required: "Category name is required",
                })}
              />
            </FloatingLabel>
            {errors.name && <Form.Text>{errors.name.message}</Form.Text>}
          </Col>

          <Col lg={12}>
            <FloatingLabel
              controlId="categoryTags"
              label="Add Tags (press enter after each tag)"
              className="custom-textarea mb-2"
            >
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add Tags (press enter after each tag)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                onBlur={addTagFromInput}
                style={{ minHeight: 96, resize: "none" }}
              />
            </FloatingLabel>

            {tags.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-2">
                {tags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="d-inline-flex align-items-center"
                    style={{
                      background: "#F3EEFF",
                      color: "#7B68EE",
                      borderRadius: 20,
                      fontSize: 13,
                      padding: "4px 10px",
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 ms-1 d-inline-flex"
                      onClick={() => removeTag(index)}
                      aria-label={`Remove ${tag}`}
                    >
                      <ModalCloseIcon />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <p className="gray mb-0" style={{ fontSize: 13, marginTop: 8 }}>
              Once saved, these categories and tags will appear when creating
              resources and quizzes.
            </p>
          </Col>
        </Row>
      </div>

      <div className="modal-footer">
        <Button type="submit" className="w-100" disabled={disable}>
          {loading ? (
            <div className="load black" />
          ) : props?.isUpdateCategory ? (
            "Update Category"
          ) : (
            "Add Category"
          )}
        </Button>
      </div>
    </Form>
  );
};

export default AddCategoryModal;
